import { yupResolver } from '@hookform/resolvers/yup';
import { useAdministrationContext } from '@openx/components-administration/lib/utils/context/AdministrationContext.tsx';
import type { UserData } from '@openx/components-administration/lib/utils/context/UserHookTypes.ts';
import type { UserFormData } from '@openx/components-administration/types.ts';
import { FormActions } from '@openx/components-core/lib/FormActions/FormActions.tsx';
import { FormContainer } from '@openx/components-core/lib/FormContainer/FormContainer.tsx';
import { scrollToError } from '@openx/components-core/utils/scrollToError.ts';
import { PageHeader } from '@openx/components-header/PageHeader.tsx';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router';
import { UserDetailsSection } from './sections/UserDetailsSection.tsx';
import { transformDataToMatchUserForm } from './utils.ts';
import { getValidationSchema } from './validationSchema.ts';

export type UserEditFormProps = {
  currentUser: UserData;
  userId: string;
};

export const UserEditForm = ({ currentUser, userId }: UserEditFormProps) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const goBackPath = location.state?.settingsGoBackPath || '/users';

  const initialValues = transformDataToMatchUserForm(currentUser);
  const { useEditUser } = useAdministrationContext();
  const { editUser } = useEditUser();

  const formMethods = useForm<UserFormData>({
    defaultValues: initialValues,
    mode: 'all',
    resolver: yupResolver(getValidationSchema(t)),
  });

  const { isDirty, isSubmitting } = formMethods.formState;

  const onSubmit = async (data: UserFormData) => {
    await editUser({
      data: {
        first_name: data.first_name,
        last_name: data.last_name,
      },
      id: userId,
    });
  };

  return (
    <PageHeader title={t('Edit User')} titlePrefix="OpenXSelect">
      <FormProvider {...formMethods}>
        <form onSubmit={formMethods.handleSubmit(onSubmit, scrollToError)}>
          <FormContainer fullPage>
            <UserDetailsSection isEdit />

            <FormActions
              submitButtonLabel={t('Save')}
              cancelButtonLabel={t('Cancel')}
              onCancel={() => navigate(goBackPath)}
              isSubmitting={isSubmitting}
              disabled={!isDirty || isSubmitting}
            />
          </FormContainer>
        </form>
      </FormProvider>
    </PageHeader>
  );
};
